// Design tokens for the Business Management System
// Single source of truth for color, spacing, radius, and typography.
//
// RULES (no exceptions):
// - Spacing: strict 8px grid. All padding/margin/gap values are multiples of 8
//   (8, 16, 24, 32). With MUI's default spacing unit of 8px this means sx values
//   of 1, 2, 3, 4 only. Form field vertical gap = 16px (gap: 2), section gap = 32px (gap: 4).
// - Radius: 6px for inputs/buttons/controls, 10px for cards/modals/drawers.
//   No pill shapes except the sidebar active state.
// - BRAND SCALE (the ONLY blues allowed anywhere — see markdown-notes/DESIGN_TOKENS.md):
//   brand-600 #2563EB = app bar + filled primary buttons (+ their hover-alpha shadows)
//   brand-700 #1D4ED8 = hover/active text & borders
//   brand-50  #EFF6FF = active-state surfaces (sidebar active, selected tab, focused rows)
//   Derived alphas of brand-600/700 (e.g. rgba(37,99,235,0.35) borders) are allowed;
//   every other blue hex is deleted. Green #15803D = success only.
//   Red #B91C1C = destructive/error only, surface #FEE2E2 = confirmation dialog.
//   Amber #B45309 = warning only. KPI numbers are neutral gray-900 by default.

export const tokens = {
  color: {
    primary: '#2563EB', // brand-600 — app bar, filled primary buttons
    primaryDark: '#1D4ED8', // brand-700 — hover
    primaryLight: '#EFF6FF', // alias of primarySurface (only allowed tint)
    primarySurface: '#EFF6FF', // brand-50 — active-state background
    success: '#15803D',
    successSurface: '#F0FDF4',
    danger: '#B91C1C',
    dangerSurface: '#FEE2E2', // confirmation-dialog background (red = intent)
    warning: '#B45309',
    warningSurface: '#FFFBEB',
    // "info" is intentionally neutral: no decorative blue banners.
    info: '#4B5563',
    infoSurface: '#F3F4F6',
    // Initials avatar (replaces the stock purple placeholder).
    avatarSurface: '#C7D2FE', // muted indigo
    avatarText: '#3730A3',
    neutral: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280', // text-muted
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  radius: {
    control: '6px', // inputs, buttons, chips, menus
    surface: '10px', // cards, modals, drawers, papers
  },
  spacing: {
    unit: 8,
    field: 16, // vertical gap between form fields
    section: 32, // vertical gap between sections
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    weights: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    // The ONE label pattern: 13px / 500 / gray-700, rendered ABOVE the input.
    label: {
      fontSize: '13px',
      fontWeight: 500,
      color: '#374151', // neutral.700
    },
    // Exactly five sizes (single family, enforced in theme.js):
    scale: {
      h1: { fontSize: '24px', lineHeight: '32px', fontWeight: 600 }, // page title
      h2: { fontSize: '16px', lineHeight: '24px', fontWeight: 600 }, // section headings
      h3: { fontSize: '14px', lineHeight: '20px', fontWeight: 600 }, // card/drawer titles
      body: { fontSize: '14px', lineHeight: '20px', fontWeight: 400 },
      caption: { fontSize: '12px', lineHeight: '16px', fontWeight: 400 }, // helper/meta
    },
  },
};

// Convenience palette mapping for MUI createTheme
export const muiPalette = {
  primary: {
    main: tokens.color.primary,
    light: tokens.color.primaryLight,
    dark: tokens.color.primaryDark,
  },
  // Secondary used to fall back to MUI's default purple (#9c27b0) — the only
  // purple in the app. Map it into the brand family instead.
  secondary: {
    main: tokens.color.primary,
    light: tokens.color.primarySurface,
    dark: tokens.color.primaryDark,
  },
  success: { main: tokens.color.success },
  warning: { main: tokens.color.warning },
  error: { main: tokens.color.danger },
  info: { main: tokens.color.info },
  grey: tokens.color.neutral,
};
